// src/Pages/Estabilishment/Notification/index.tsx

import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import { toast } from "react-toastify";
import { useT } from "../../../i18n";
import type { PushAudience } from "../../../Services/Notification";
import {
  getNotificationStats,
  postNotification,
  INotificationStats,
  IRestNotificationStats,
  IRestNotificationSend,
} from "../../../Services/Notification";

// ─── Pertalks suggested messages ─────────────────────────────────────────────

// Os textos ficam em português: eles são a própria chave de tradução (ver src/i18n).
const SUGGESTIONS = [
  "Temos uma oferta especial esperando por você! Venha nos visitar agora. 🎉",
  "Novidade no cardápio hoje! Não perca essa experiência única. 🍽️",
  "Evento especial acontecendo agora no nosso espaço. Você está a poucos passos! 🎶",
  "Desconto exclusivo para você que está no app. Aproveite agora! 🏷️",
  "Que tal um mimo especial? Venha buscar a sua surpresa! 🎁",
  "Hoje tem promoção imperdível. Aproveite enquanto durar! ⚡",
];

const MAX_CHARS = 120;

/** Cota de um público, com o bônus de missões que o backend manda à parte. */
type Quota = INotificationStats["online"] & { bonus?: number };

// ─── Icons ────────────────────────────────────────────────────────────────────

const ic = (p: React.ReactNode) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{p}</svg>
);

const IconBell   = () => ic(<><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></>);
const IconSend   = () => ic(<><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></>);
const IconUsers  = () => ic(<><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></>);
const IconStar   = () => ic(<><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></>);

// ─── Component ────────────────────────────────────────────────────────────────

export const Notification = () => {
  const navigate = useNavigate();
  const t = useT();
  const [stats,       setStats]       = useState<INotificationStats | null>(null);
  const [statsLoading,setStatsLoading]= useState(true);
  const [title,       setTitle]       = useState("");
  const [text,        setText]        = useState("");
  const [sending,     setSending]     = useState(false);
  const [sent,        setSent]        = useState<{ sent: number; errors: number } | null>(null);
  const textareaRef                   = useRef<HTMLTextAreaElement>(null);

  const loadStats = () => {
    setStatsLoading(true);
    getNotificationStats()
      .then((res) => {
        const { data } = res as IRestNotificationStats;
        setStats(data.data);
      })
      .catch(() => toast.error(t("Erro ao carregar estatísticas.")))
      .finally(() => setStatsLoading(false));
  };

  useEffect(() => { loadStats(); }, []);

  // público do disparo — cada um tem sua própria cota mensal
  const [audience, setAudience] = useState<PushAudience>("online");

  const handleSuggestion = (suggestion: string) => {
    const truncated = t(suggestion).slice(0, MAX_CHARS);
    setText(truncated);
    textareaRef.current?.focus();
  };

  const handleSend = () => {
    if (!title.trim()) { toast.warning(t("Informe um título.")); return; }
    if (!text.trim())  { toast.warning(t("Informe o texto da mensagem.")); return; }
    if (text.length > MAX_CHARS) { toast.warning(t("Texto deve ter no máximo {max} caracteres.", { max: MAX_CHARS })); return; }

    setSending(true);
    setSent(null);

    postNotification(title.trim(), text.trim(), audience)
      .then((res) => {
        const { data } = res as IRestNotificationSend;
        const sentCount = data?.data?.sent ?? 0;
        setSent({ sent: sentCount, errors: data?.data?.errors ?? 0 });

        if (sentCount > 0) {
          // Envio real bem-sucedido
          // Informa as falhas do FCM. Sem isso, "3 enviadas" para um público
          // de 5 parece erro do sistema, quando na verdade são tokens
          // expirados ou apps desinstalados.
          const falhas = data?.data?.errors ?? 0;
          toast.success(
            falhas > 0
              ? t(
                  "{n} notificação(ões) enviada(s). {falhas} não chegaram (aparelho sem o app ou notificações desativadas).",
                  { n: sentCount, falhas },
                )
              : t("{n} notificação(ões) enviada(s) com sucesso!", { n: sentCount }),
          );
          setTitle("");
          setText("");
        } else {
          // Nenhum destinatário: usa a mensagem que o backend já explica
          // (sem usuários ativos, sem token, etc.) — NÃO trata como sucesso.
          toast.warning(
            data?.message ??
              t("No momento não há usuários ativos para receber a notificação. Tente novamente mais tarde."),
          );
        }
        loadStats();
      })
      .catch((err: any) => {
        const code = err?.response?.status;
        if (code === 429) {
          toast.error(t("Limite mensal de disparos atingido."));
        } else if (code === 403) {
          // plano não permite push — sincroniza a tela recarregando stats
          toast.error(
            err?.response?.data?.message ??
              t("Recurso disponível apenas no plano Connection."),
          );
          loadStats();
        } else {
          toast.error(err?.response?.data?.message ?? t("Erro ao enviar notificação."));
        }
      })
      .finally(() => setSending(false));
  };

  // ── Styles ────────────────────────────────────────────────────────────────

  const card: React.CSSProperties   = { background: "#fff", borderRadius: 14, border: "1px solid #f0f0f0", padding: "20px 24px", marginBottom: 16 };
  const sTitle: React.CSSProperties = { fontSize: 13, fontWeight: 600, color: "#999", letterSpacing: 1, textTransform: "uppercase", marginBottom: 14 };
  // cota do público selecionado.
  //
  // `bonus` são disparos extras conquistados em missões (Gameficação Places).
  // O backend já soma esse valor dentro de `limit`; o campo vem separado só
  // para conseguirmos mostrar de onde veio. Tipado aqui em vez de em
  // INotificationStats para o campo continuar opcional enquanto a API antiga
  // ainda estiver no ar.
  const quota: Quota | null = stats
    ? ((audience === "offline" ? stats.offline : stats.online) as Quota)
    : null;

  const bonus = quota?.bonus ?? 0;

  const limitPct = quota ? Math.min(100, Math.round((quota.used / Math.max(quota.limit, 1)) * 100)) : 0;
  const barColor = limitPct < 50 ? "#00b6bc" : limitPct < 80 ? "#f59e0b" : "#ef4444";

  // planType vem normalizado do backend: insight | connection | pulse | wave | none
  const planType = (stats?.planType ?? "none").toLowerCase();
  const planAllowsPush = planType === "connection";

  // Connection com documentos ainda em análise: o plano permite, mas a área
  // só abre depois da aprovação manual.
  const pendingApproval = !!stats?.pendingApproval;

  const canSend = !!quota && quota.remaining > 0 && !sending && planAllowsPush && !pendingApproval;
  const charsLeft = MAX_CHARS - text.length;

  // ── Render ────────────────────────────────────────────────────────────────

  if (statsLoading) return (
    <div style={{ display: "flex", justifyContent: "center", paddingTop: 60 }}>
      <CircularProgress size={22} style={{ color: "#00b6bc" }} />
    </div>
  );

  // Aguardando aprovação dos documentos — plano correto, área ainda fechada
  if (stats && planAllowsPush && pendingApproval) return (
    <div style={{ maxWidth: 560, padding: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 24 }}>
        <span style={{ color: "#00b6bc" }}><IconBell /></span>
        <h2 style={{ fontSize: 24, fontWeight: 700, color: "#111", margin: 0 }}>{t("Notificações Push")}</h2>
      </div>
      <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #f0f0f0", padding: "40px 32px", textAlign: "center" }}>
        <div style={{ fontSize: 40, marginBottom: 16 }}>⏳</div>
        <h3 style={{ fontSize: 18, fontWeight: 700, color: "#111", margin: "0 0 10px" }}>
          {stats?.estabilishmentName
            ? t("Estamos analisando os documentos de {nome}", { nome: stats.estabilishmentName })
            : t("Estamos analisando os documentos")}
        </h3>
        <p style={{ fontSize: 14, color: "#888", margin: "0 0 8px", lineHeight: 1.6 }}>
          {t("As notificações desta unidade são liberadas assim que a análise for concluída — o prazo é de até")}{" "}
          <strong>{t("72 horas")}</strong>.
        </p>
        <p style={{ fontSize: 13, color: "#888", margin: "0 0 8px", lineHeight: 1.6 }}>
          {t("Se você tem outra unidade já aprovada, troque no seletor do Dashboard para disparar por ela normalmente.")}
        </p>
        <p style={{ fontSize: 13, color: "#aaa", margin: 0, lineHeight: 1.6 }}>
          {t("Enquanto isso, o restante do painel continua disponível.")}
          {stats?.documentsStatus === "rejected" &&
            " " + t("Encontramos uma divergência nos documentos — verifique seu e-mail.")}
        </p>
      </div>
    </div>
  );

  if (!stats || !planAllowsPush) return (
    <div style={{ maxWidth: 560, padding: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 24 }}>
        <span style={{ color: "#00b6bc" }}><IconBell /></span>
        <h2 style={{ fontSize: 24, fontWeight: 700, color: "#111", margin: 0 }}>{t("Notificações Push")}</h2>
      </div>
      <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #f0f0f0", padding: "40px 32px", textAlign: "center" }}>
        <div style={{ fontSize: 40, marginBottom: 16 }}>🔔</div>
        <h3 style={{ fontSize: 18, fontWeight: 700, color: "#111", margin: "0 0 10px" }}>
          {t("Disponível no Plano CONNECTION")}
        </h3>
        <p style={{ fontSize: 14, color: "#888", margin: "0 0 24px", lineHeight: 1.6 }}>
          {t("O envio de notificações push personalizadas é uma funcionalidade exclusiva do plano")}{" "}
          <strong>CONNECTION</strong>.
        </p>
        <button
          onClick={() => navigate("/establishment/subscription")}
          style={{ padding: "11px 28px", background: "#00b6bc", color: "#fff", border: "none", borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: "pointer" }}
        >
          {t("Ver planos disponíveis")}
        </button>
      </div>
    </div>
  );

  return (
    <div style={{ maxWidth: "98%" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
        <span style={{ color: "#00b6bc" }}><IconBell /></span>
        <h2 style={{ fontSize: 24, fontWeight: 700, color: "#111", margin: 0 }}>{t("Notificações Push")}</h2>
      </div>

      {/* Deixa claro qual estabelecimento receberá o disparo — a escolha vem
          do seletor do painel e não é óbvia nesta tela. */}
      {stats?.estabilishmentName && (
        <p style={{ margin: "0 0 20px", fontSize: 13, color: "#888" }}>
          {t("Enviando para clientes de")}{" "}
          <strong style={{ color: "#555" }}>{stats.estabilishmentName}</strong>.{" "}
          {t("Para trocar, use o seletor no Dashboard.")}
        </p>
      )}

      {/* ── Seletor de público ──────────────────────────────────────────── */}
      <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
        {([
          { key: "online"  as PushAudience, label: t("Quem está no local agora"), hint: t("Check-in ativo") },
          { key: "offline" as PushAudience, label: t("Quem já visitou"),          hint: t("Já fez check-in") },
        ]).map((opt) => {
          const q = (opt.key === "offline" ? stats.offline : stats.online) as Quota;
          const active = audience === opt.key;
          const qBonus = q.bonus ?? 0;
          return (
            <button
              key={opt.key}
              onClick={() => setAudience(opt.key)}
              style={{
                flex: 1, textAlign: "left", cursor: "pointer",
                border: active ? "2px solid #00b6bc" : "1.5px solid #e8e8e8",
                background: active ? "#f0fdfe" : "#fff",
                borderRadius: 12, padding: "12px 14px",
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 700, color: "#111", marginBottom: 2 }}>
                {opt.label}
              </div>
              <div style={{ fontSize: 11, color: "#999", marginBottom: 6 }}>{opt.hint}</div>
              <div style={{ fontSize: 12, color: active ? "#00b6bc" : "#666", fontWeight: 600 }}>
                {t("{n} de {total} disparos · {pessoas} pessoa(s)", {
                  n: q.remaining,
                  total: q.limit,
                  pessoas: q.audienceSize,
                })}
              </div>
              {qBonus > 0 && (
                <div
                  style={{
                    display: "inline-block",
                    marginTop: 6,
                    padding: "2px 8px",
                    borderRadius: 999,
                    background: "#e6f9fa",
                    color: "#0b6f73",
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                >
                  {t("+{n} de missões", { n: qBonus })}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* ── Stats card ──────────────────────────────────────────────────── */}
      <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #f0f0f0", padding: "20px 24px", marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 24, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: "#555" }}>
                {t("Disparos este mês · {publico}", {
                  publico: audience === "offline" ? t("quem já visitou") : t("quem está no local"),
                })}
              </span>
              <span style={{ fontSize: 13, fontWeight: 700, color: barColor }}>
                {quota!.used}/{quota!.limit}
              </span>
            </div>
            <div style={{ height: 8, background: "#f0f0f0", borderRadius: 999, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${limitPct}%`, background: barColor, borderRadius: 999, transition: "width .3s" }} />
            </div>
            <p style={{ margin: "8px 0 0", fontSize: 12, color: "#999" }}>
              {quota!.remaining > 0
                ? t("{n} disparo(s) restante(s) este mês", { n: quota!.remaining })
                : t("Limite mensal atingido")}
            </p>
            {bonus > 0 && (
              <p style={{ margin: "4px 0 0", fontSize: 12, color: "#0b6f73" }}>
                {t("Inclui")} <strong>{bonus}</strong>{" "}
                {t("disparo(s) extra(s) conquistado(s) em missões — valem até o fim deste mês.")}
              </p>
            )}
          </div>

          <div style={{ background: "#e6f9fa", borderRadius: 12, padding: "14px 20px", textAlign: "center", minWidth: 130 }}>
            <div style={{ fontSize: 26, fontWeight: 700, color: "#00b6bc", lineHeight: 1 }}>
              {quota!.audienceSize}
            </div>
            <div style={{ fontSize: 12, color: "#0b6f73", marginTop: 4 }}>
              {audience === "offline" ? t("já visitaram") : t("no local agora")}
            </div>
          </div>
        </div>
      </div>

      {/* ── Compose card ────────────────────────────────────────────────── */}
      <div style={card}>
        <p style={sTitle}>{t("Nova mensagem")}</p>

        {/* Title input */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#444", marginBottom: 6 }}>
            {t("Título")} <span style={{ color: "#e53935" }}>*</span>
          </label>
          <input
            type="text"
            placeholder={t("Ex: Oferta especial!")}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={80}
            style={{
              width: "100%", boxSizing: "border-box",
              border: "1.5px solid #e8e8e8", borderRadius: 10,
              padding: "11px 14px", fontSize: 14, color: "#222",
              outline: "none", background: "#fff",
            }}
            onFocus={(e) => (e.currentTarget.style.borderColor = "#00b6bc")}
            onBlur={(e)  => (e.currentTarget.style.borderColor = "#e8e8e8")}
          />
        </div>

        {/* Suggestions */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
            <span style={{ color: "#00b6bc" }}><IconStar /></span>
            <span style={{ fontSize: 13, fontWeight: 600, color: "#555" }}>{t("Sugestões Pertalks")}</span>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {SUGGESTIONS.map((s, i) => (
              <button
                key={i}
                type="button"
                onClick={() => handleSuggestion(s)}
                style={{
                  padding: "5px 12px", borderRadius: 999, fontSize: 12,
                  border: "1.5px solid #00b6bc", background: "#fff",
                  color: "#00b6bc", cursor: "pointer", fontWeight: 500,
                  transition: "background .15s",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "#e6f9fa"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; }}
              >
                {t(s).length > 30 ? t(s).slice(0, 30) + "…" : t(s)}
              </button>
            ))}
          </div>
          <p style={{ fontSize: 12, color: "#aaa", marginTop: 6 }}>
            {t("Clique em uma sugestão para preencher o texto. Você pode editar antes de enviar.")}
          </p>
        </div>

        {/* Text area */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
            <label style={{ fontSize: 13, fontWeight: 600, color: "#444" }}>
              {t("Mensagem")} <span style={{ color: "#e53935" }}>*</span>
            </label>
            <span style={{ fontSize: 12, fontWeight: 600, color: charsLeft < 20 ? "#ef4444" : "#aaa" }}>
              {text.length}/{MAX_CHARS}
            </span>
          </div>
          <textarea
            ref={textareaRef}
            placeholder={t("Digite sua mensagem ou use uma sugestão acima…")}
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, MAX_CHARS))}
            rows={4}
            style={{
              width: "100%", boxSizing: "border-box", resize: "vertical",
              border: `1.5px solid ${charsLeft < 0 ? "#ef4444" : "#e8e8e8"}`,
              borderRadius: 10, padding: "11px 14px",
              fontSize: 14, color: "#222", outline: "none",
              background: "#fff", lineHeight: 1.5,
            }}
            onFocus={(e) => (e.currentTarget.style.borderColor = "#00b6bc")}
            onBlur={(e)  => (e.currentTarget.style.borderColor = "#e8e8e8")}
          />
          {charsLeft < 0 && (
            <p style={{ fontSize: 12, color: "#ef4444", marginTop: 4 }}>
              {t("Texto excede o limite de {max} caracteres.", { max: MAX_CHARS })}
            </p>
          )}
        </div>

        {/* Send button */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 13, color: "#aaa" }}>
            {quota && quota.audienceSize > 0
              ? (audience === "offline"
                  ? t("Será enviado para {n} pessoa(s) que já visitaram seu estabelecimento", { n: quota.audienceSize })
                  : t("Será enviado para {n} usuário(s) com check-in ativo", { n: quota.audienceSize }))
              : t("Nenhum usuário ativo no momento")}
          </span>
          <button
            type="button"
            onClick={handleSend}
            disabled={!canSend || text.length === 0 || title.length === 0 || charsLeft < 0}
            style={{
              display: "flex", alignItems: "center", gap: 8,
              padding: "11px 22px", borderRadius: 10,
              border: "none", fontWeight: 700, fontSize: 14,
              cursor: (!canSend || text.length === 0 || title.length === 0) ? "not-allowed" : "pointer",
              background: (!canSend || text.length === 0 || title.length === 0) ? "#e0e0e0" : "#00b6bc",
              color: (!canSend || text.length === 0 || title.length === 0) ? "#aaa" : "#fff",
              transition: "background .15s",
            }}
          >
            {sending
              ? <CircularProgress size={16} color="inherit" />
              : <IconSend />}
            {sending ? t("Enviando…") : t("Enviar notificação")}
          </button>
        </div>

        {/* Last send result */}
        {sent && (
          <div style={{
            marginTop: 16, padding: "10px 14px",
            background: sent.sent > 0 ? "#e6f7ef" : "#fef2f2",
            borderRadius: 10, fontSize: 13,
            color: sent.sent > 0 ? "#1a7a4a" : "#b91c1c",
          }}>
            {t("Último envio:")} <strong>{sent.sent}</strong> {t("entregue(s)")}
            {sent.errors > 0 && t(", {n} falha(s)", { n: sent.errors })}.
          </div>
        )}
      </div>

      {/* ── Limit warning ────────────────────────────────────────────────── */}
      {quota && quota.remaining === 0 && (
        <div style={{
          padding: "14px 18px", background: "#fef2f2",
          borderRadius: 12, border: "1px solid #fecaca",
          fontSize: 13, color: "#b91c1c",
        }}>
          {t("Você atingiu o limite de")}{" "}
          <strong>{t("{n} disparos", { n: quota.limit })}</strong>{" "}
          {t("para este público neste mês. O limite será renovado no dia 1 do próximo mês.")}
        </div>
      )}
    </div>
  );
};